import React, { useEffect, useState } from 'react';
import { Drawer, Button, List, Typography, Tag, Space, Switch, Empty, Input, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

/**
 * Drawer de revisión de correcciones de redacción IA antes de firmar.
 * Muestra, por cada texto cambiado, original -> sugerido con un toggle de
 * aceptar/rechazar (los cambiados vienen pre-aceptados). Al confirmar entrega
 * un map key -> texto final (sugerido si aceptado, original si rechazado).
 *
 * Cada fila además permite "Regenerar": el técnico escribe una instrucción
 * libre (ej. "más formal", "más corto") y se reemplaza la sugerencia de ESA
 * fila. La regeneración la resuelve el padre vía onRegenerate.
 *
 * Props:
 * - open: boolean
 * - loading: boolean
 * - items: Array<{ key, label, original, improved, changed }>
 * - onConfirm: (Record<string,string>) => void   // key -> texto final elegido
 * - onSkip: () => void                            // continuar sin aplicar
 * - onRegenerate: (key, instruction) => Promise<boolean>  // regenera una fila
 */
const RevisarRedaccionDrawer = ({ open, loading, items = [], onConfirm, onSkip, onRegenerate }) => {
  const changedItems = items.filter((i) => i.changed);
  const [accepted, setAccepted] = useState({});
  const [applying, setApplying] = useState(false);
  // key de la fila con el input de regeneración abierto (solo una a la vez).
  const [regenKey, setRegenKey] = useState(null);
  const [regenInstruction, setRegenInstruction] = useState('');
  // key de la fila que está regenerando (spinner por fila).
  const [regenLoadingKey, setRegenLoadingKey] = useState(null);

  useEffect(() => {
    // Pre-aceptar todos los cambiados cada vez que se abre con nuevos items.
    const initial = {};
    changedItems.forEach((i) => {
      initial[i.key] = true;
    });
    setAccepted(initial);
    setRegenKey(null);
    setRegenInstruction('');
    setRegenLoadingKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, items]);

  const toggle = (key, value) => setAccepted((prev) => ({ ...prev, [key]: value }));

  const openRegen = (key) => {
    setRegenKey(key);
    setRegenInstruction('');
  };

  const cancelRegen = () => {
    setRegenKey(null);
    setRegenInstruction('');
  };

  const submitRegen = async (key) => {
    const instruction = regenInstruction.trim();
    if (!instruction) return;
    setRegenLoadingKey(key);
    try {
      const ok = await onRegenerate?.(key, instruction);
      if (ok) cancelRegen();
    } finally {
      setRegenLoadingKey(null);
    }
  };

  const handleConfirm = async () => {
    const result = {};
    changedItems.forEach((i) => {
      result[i.key] = accepted[i.key] ? i.improved : i.original;
    });
    setApplying(true);
    try {
      await onConfirm(result);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Drawer
      title="Revisar redacción antes de firmar"
      placement="right"
      width={560}
      open={open}
      onClose={onSkip}
      footer={
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onSkip}>Omitir y firmar</Button>
          <Button type="primary" onClick={handleConfirm} disabled={loading} loading={applying}>
            {applying ? 'Aplicando…' : 'Aplicar y continuar'}
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
          <div style={{ marginTop: 12, color: '#64748b' }}>Analizando la redacción con IA…</div>
        </div>
      ) : changedItems.length === 0 ? (
        <Empty description="No hay correcciones sugeridas. Podés continuar a firmar." />
      ) : (
        <List
          dataSource={changedItems}
          rowKey={(i) => i.key}
          renderItem={(item) => (
            <List.Item key={item.key}>
              <div style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }} align="start">
                  <Text strong>{item.label}</Text>
                  <Space>
                    <Text type="secondary">Aplicar</Text>
                    <Switch
                      size="small"
                      checked={!!accepted[item.key]}
                      onChange={(v) => toggle(item.key, v)}
                    />
                  </Space>
                </Space>
                <Paragraph type="secondary" delete style={{ marginBottom: 4, marginTop: 8 }}>
                  {item.original}
                </Paragraph>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Tag color="green">Sugerido</Tag> {item.improved}
                </Paragraph>

                {regenKey === item.key ? (
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      size="small"
                      autoFocus
                      placeholder="Decile cómo mejorarlo: más formal, más corto, mencioná la norma…"
                      value={regenInstruction}
                      onChange={(e) => setRegenInstruction(e.target.value)}
                      onPressEnter={() => submitRegen(item.key)}
                      disabled={regenLoadingKey === item.key}
                    />
                    <Button
                      size="small"
                      type="primary"
                      loading={regenLoadingKey === item.key}
                      disabled={!regenInstruction.trim()}
                      onClick={() => submitRegen(item.key)}
                    >
                      Regenerar
                    </Button>
                    <Button
                      size="small"
                      onClick={cancelRegen}
                      disabled={regenLoadingKey === item.key}
                    >
                      Cancelar
                    </Button>
                  </Space.Compact>
                ) : (
                  <Button
                    size="small"
                    type="link"
                    icon={<ReloadOutlined />}
                    style={{ paddingLeft: 0 }}
                    onClick={() => openRegen(item.key)}
                  >
                    Regenerar con otra indicación
                  </Button>
                )}
              </div>
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
};

export default RevisarRedaccionDrawer;
